<template>
  <div :class="['border-r border-gray-200 bg-white shrink-0 flex flex-col transition-all duration-200', collapsed ? 'w-12' : 'w-80']">
    <div class="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 shrink-0">
      <div v-if="!collapsed" class="flex items-baseline gap-3">
        <router-link to="/pages" class="no-underline text-primary-600 font-semibold text-[13px]">Pages</router-link>
        <router-link v-if="canAdmin" :to="{ name: 'AdminAgentPages' }" class="no-underline text-gray-500 text-[13px] hover:text-primary-600">Admin</router-link>
      </div>
      <button type="button" class="p-1 btn-primary" :class="{ 'mx-auto': collapsed }" @click="collapsed = !collapsed">
        <font-awesome-icon :icon="['fas', collapsed ? 'chevron-right' : 'chevron-left']" class="w-4 h-4" />
      </button>
    </div>
    <template v-if="!collapsed">
      <div class="px-3 py-2 border-b border-gray-100 shrink-0">
        <input v-model="query" type="search" placeholder="Search pages" class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
      </div>
      <div class="flex-1 overflow-y-auto p-2">
        <LoadingIndicator v-if="loading" message="Loading pages..." />
        <p v-if="error" class="text-sm text-red-700 p-2">{{ error }}</p>
        <template v-if="query.trim()">
          <router-link
            v-for="hit in hits" :key="hit.key" :to="hit.to"
            class="flex items-center gap-2 rounded-md px-3 py-1.5 no-underline hover:bg-gray-100"
            :class="{ 'bg-primary-100': isActive(hit.to) }"
          >
            <font-awesome-icon :icon="hit.icon" :title="hit.hint" :class="['text-xs shrink-0', hit.tone || 'text-gray-400']" />
            <span class="min-w-0">
              <span class="block truncate text-[13px] font-medium text-gray-800" :class="{ sensitive: hit.sensitive }" data-spoof-kind="label">{{ hit.label }}</span>
              <span v-if="hit.where" class="block truncate text-[11px] text-gray-500" :class="{ sensitive: hit.sensitive }" data-spoof-kind="label">{{ hit.where }}</span>
            </span>
          </router-link>
          <p v-if="!hits.length" class="text-sm text-gray-500 px-3 py-2">No pages match.</p>
        </template>
        <template v-else>
          <div
            v-if="canAdmin"
            class="text-[11px] text-gray-400 px-3 py-1 rounded border border-dashed border-transparent"
            :class="{ 'border-primary-400 bg-primary-50 text-primary-700': rootDrop }"
            @dragover.prevent="rootDrop = true"
            @dragleave="rootDrop = false"
            @drop.prevent="onRootDrop"
          >Drag pages or folders to move or reorder</div>
          <PagesTreeNode
            v-for="node in nodes" :key="node.key"
            :node="node" :expanded="expanded" :active-path="$route.path" :can-drag="canAdmin"
            @toggle="toggle"
            @node-drop="$emit('node-drop', $event)"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingIndicator from '../widgetcomponents/library/LoadingIndicator.vue';
import PagesTreeNode from './PagesTreeNode.vue';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';

export default {
  name: 'PagesExplorer',
  components: { FontAwesomeIcon, LoadingIndicator, PagesTreeNode },
  props: {
    nodes: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  emits: ['node-drop'],
  data() {
    return { collapsed: false, query: '', expanded: {}, rootDrop: false };
  },
  computed: {
    canAdmin() {
      return canAccessFeature(FEATURES.MODULE_ADMIN);
    },
    hits() {
      const needle = this.query.trim().toLowerCase();
      const found = [];
      const walk = (node, trail) => {
        if (node.label.toLowerCase().includes(needle)) {
          found.push({ key: node.key, label: node.label, to: node.to, icon: node.icon, tone: node.tone, hint: node.hint, sensitive: node.sensitive, where: trail.join(' / ') });
        }
        (node.children || []).forEach((child) => walk(child, [...trail, node.label]));
      };
      this.nodes.forEach((node) => walk(node, []));
      return found.slice(0, 50);
    },
  },
  watch: {
    '$route.path': 'reveal',
    nodes: { immediate: true, handler: 'reveal' },
  },
  methods: {
    toggle(key) {
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] };
    },
    onRootDrop(e) {
      this.rootDrop = false;
      try {
        const source = JSON.parse(e.dataTransfer.getData('application/json') || '{}');
        if (!source.key) return;
        this.$emit('node-drop', { source, target: { kind: 'folder', stem: '' }, position: 'inside' });
      } catch {
        // ignore invalid drag payload
      }
    },
    isActive(to) {
      return this.$router.resolve(to).path === this.$route.path;
    },
    reveal() {
      const open = { ...this.expanded };
      const find = (node, trail) => {
        if (node.to && this.isActive(node.to)) return trail;
        for (const child of node.children || []) {
          const hit = find(child, [...trail, node.key]);
          if (hit) return hit;
        }
        return null;
      };
      this.nodes.forEach((node) => (find(node, []) || []).forEach((key) => { open[key] = true; }));
      this.expanded = open;
    },
  },
};
</script>
